import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { formatCurrency, getActiveCurrencyCodeLabel } from "utils";
import ActionErrorMsg from "./ActionErrorMsg";
import { BiBookmark } from "react-icons/bi";
import { HiHeart, HiOutlineHeart, HiOutlineShoppingCart } from "react-icons/hi";
import { RiLeafLine } from "react-icons/ri";
import { MdOutlineAutoAwesome } from "react-icons/md";
import { BookFormat } from "utils/server-constants/bookFormats";

const ActionsCard = ({
	price,
	physicalPrice,
	currency,
	book,
	addToPicks,
	isAddingToPicks,
	handleAddToPicks,
	removeFromPicks,
	isRemovingFromLibrary,
	removeFromLibrary,
	addToCart,
	isAddingToCart,
	handleAddToCart,
	cartQuantity,
	handleChangeCartQuantity,
	hasRoles,
	Role,
	format,
	setFormat,
	isInWishlist,
	handleWishlistToggle,
	isAddingToWishlist,
	isRemovingFromWishlist,
}) => {
	const physicalAvailable = book.format === BookFormat.Physical || book.format === BookFormat.Both;

	const digitalAvailable = book.format === BookFormat.Digital || book.format === BookFormat.Both;

	const canToggleDigital = digitalAvailable;
	const canTogglePhysical = physicalAvailable;

	return (
		<div className="space-y-5 md:space-y-6">
			<div className="rounded-2xl border border-akgreener/15 bg-gradient-to-br from-akgreener/10 via-white to-akgreenest/5 p-4 md:p-5">
				<div className="flex items-center justify-between gap-3">
					<div>
						<p className="text-xs uppercase tracking-wider text-gray-500">Now reading</p>
						<p className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-akgreenest">
							<span className="h-2 w-2 rounded-full bg-akgreener animate-pulse" />
							{format === "digital" ? "Digital edition" : "Physical copy"}
						</p>
					</div>
					<div className="rounded-full border border-akgreener/30 bg-white px-3 py-1.5 text-xs font-semibold text-akgreenest">
						{book?.isFree ? "Free" : "Premium"}
					</div>
				</div>

				<div className="mt-4 flex items-end justify-between gap-3">
					<div>
						<p className="text-xs text-gray-500">Price</p>
						<h3 className="text-3xl md:text-3xl font-bold tracking-tight text-gray-600">
							{getActiveCurrencyCodeLabel(currency)}{" "}
							{formatCurrency(format === "digital" ? price : physicalPrice).replace("$", "")}
						</h3>
					</div>
					<div className="hidden md:flex items-center gap-1 rounded-full border border-akgreener/20 bg-white/90 px-2.5 py-1 text-xs text-gray-600">
						<MdOutlineAutoAwesome className="h-3.5 w-3.5 text-akgreener" />
						Fast checkout
					</div>
				</div>
			</div>

			<fieldset className="space-y-3">
				<legend className="text-xs tracking-wider uppercase text-gray-500 font-semibold">
					Choose format
				</legend>
				<div className="grid grid-cols-2 gap-2 md:gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-2">
					<button
						type="button"
						disabled={!canToggleDigital}
						aria-disabled={!canToggleDigital}
						aria-pressed={format === "digital"}
						onClick={() => setFormat("digital")}
						className={`relative px-3 md:px-4 py-3 min-h-[44px] rounded-xl border text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-akgreener ${
							format === "digital"
								? "border-akgreener/40 bg-white shadow-sm text-gray-900"
								: "border-transparent bg-transparent text-gray-600"
						} ${
							canToggleDigital
								? "cursor-pointer hover:-translate-y-0.5 hover:border-akgreener/20 hover:bg-white/95 active:translate-y-0"
								: "cursor-not-allowed opacity-50"
						}`}
					>
						<p className="font-semibold text-sm md:text-base mb-1">Digital</p>
						<p className="text-xs md:text-sm opacity-90">
							{getActiveCurrencyCodeLabel(currency)} {formatCurrency(price).replace("$", "")}
						</p>
					</button>

					<button
						type="button"
						disabled={!canTogglePhysical}
						aria-disabled={!canTogglePhysical}
						aria-pressed={format === "physical"}
						onClick={() => setFormat("physical")}
						className={`relative px-3 md:px-4 py-3 min-h-[44px] rounded-xl border text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-akgreener ${
							format === "physical"
								? "border-akgreener/40 bg-white shadow-sm text-gray-900"
								: "border-transparent bg-transparent text-gray-600"
						} ${
							canTogglePhysical
								? "cursor-pointer hover:-translate-y-0.5 hover:border-akgreener/20 hover:bg-white/95 active:translate-y-0"
								: "cursor-not-allowed opacity-50"
						}`}
					>
						<p className="font-semibold text-sm md:text-base mb-1">Physical</p>
						<p className="text-xs md:text-sm opacity-90">
							{getActiveCurrencyCodeLabel(currency)}{" "}
							{formatCurrency(physicalPrice).replace("$", "")}
						</p>
					</button>
				</div>
			</fieldset>

			<div className="relative overflow-hidden rounded-xl border border-gray-100 bg-white p-3.5 md:p-4">
				<div className="pointer-events-none absolute -right-5 -top-5 h-14 w-14 rounded-full border border-akgreener/20" />
				<p className="text-xs md:text-sm text-gray-600 pr-6">
					{format === "digital"
						? "Read instantly in the Akello Library app. Your book is ready the moment checkout completes."
						: "Get a printed copy delivered for a classic reading experience and shelf-worthy collection."}
				</p>
				<div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-akgreenest">
					<RiLeafLine className="h-3.5 w-3.5" />
					Simple. secure. seamless.
				</div>
			</div>

			{!Boolean(book?.isFree) &&
				(addToCart.possible ? (
					<div className="space-y-3 md:space-y-4 pt-2 border-t border-gray-100">
						{hasRoles([Role.INSTITUTION, Role.EDUCATOR]) && (
							<div className="flex items-center gap-2 md:gap-3">
								<label htmlFor="book-quantity" className="text-xs md:text-sm text-gray-600">
									Quantity:
								</label>
								<input
									id="book-quantity"
									type="number"
									min={1}
									value={cartQuantity}
									onChange={(e) => handleChangeCartQuantity(e.target.value)}
									className="w-16 md:w-20 px-2 md:px-3 py-1.5 md:py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-akgreener/40 focus:border-akgreener"
								/>
							</div>
						)}

						<button
							type="button"
							onClick={handleAddToCart}
							className="w-full py-3 px-4 min-h-[44px] bg-gradient-to-r from-akgreenest to-akgreener text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-akgreener/25 active:translate-y-0 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-akgreener/60"
						>
							<HiOutlineShoppingCart className="w-4 h-4 md:w-5 md:h-5" />
							<span className="text-sm md:text-base">Add to Cart</span>
							{isAddingToCart && (
								<AiOutlineLoading3Quarters className="w-4 h-4 md:w-5 md:h-5 animate-spin ml-2" />
							)}
						</button>
					</div>
				) : (
					<ActionErrorMsg
						heading="Add to cart"
						message={addToCart.message}
						action={addToCart.action}
						className="w-full py-3 px-4 min-h-[44px] rounded-xl border border-gray-200 bg-gray-100 text-gray-500 text-sm md:text-base font-medium"
					>
						Add to cart
					</ActionErrorMsg>
				))}

			{/* Wishlist Section */}
			{isInWishlist !== undefined && handleWishlistToggle && (
				<div className="space-y-2 md:space-y-3">
					{isInWishlist ? (
						<button
							type="button"
							onClick={handleWishlistToggle}
							disabled={isAddingToWishlist || isRemovingFromWishlist}
							className="w-full py-2.5 md:py-3 px-4 min-h-[44px] border border-red-500/80 text-red-500 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-red-50 transition-all duration-200 text-sm md:text-base"
						>
							<HiHeart className="w-4 h-4 md:w-5 md:h-5" />
							Remove from wishlist
							{(isAddingToWishlist || isRemovingFromWishlist) && (
								<AiOutlineLoading3Quarters className="w-4 h-4 md:w-5 md:h-5 animate-spin ml-2" />
							)}
						</button>
					) : (
						<button
							type="button"
							onClick={handleWishlistToggle}
							disabled={isAddingToWishlist || isRemovingFromWishlist}
							className="w-full py-2.5 md:py-3 px-4 min-h-[44px] border border-akgreener text-akgreener rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-akgreener/5 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
						>
							<HiOutlineHeart className="w-4 h-4 md:w-5 md:h-5" />
							<span className="text-sm md:text-base">Add to wishlist</span>
							{(isAddingToWishlist || isRemovingFromWishlist) && (
								<AiOutlineLoading3Quarters className="w-4 h-4 md:w-5 md:h-5 animate-spin ml-2" />
							)}
						</button>
					)}
				</div>
			)}

			{/* Picks Section */}
			<div className="space-y-2 md:space-y-3 pt-1">
				<p className="text-xs text-gray-500 leading-relaxed">
					Use Picks if you are on a subscription plan and want to unlock this title within your book
					limit.
				</p>
				{addToPicks.possible ? (
					<button
						type="button"
						onClick={handleAddToPicks}
						className="w-full py-2.5 md:py-3 px-4 min-h-[44px] border border-akgreener/40 text-akgreenest rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-akgreener/5 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-akgreener/50"
					>
						<BiBookmark className="w-4 h-4 md:w-5 md:h-5" />
						<span className="text-sm md:text-base">Add to Picks</span>
						{isAddingToPicks && (
							<AiOutlineLoading3Quarters className="w-4 h-4 md:w-5 md:h-5 animate-spin ml-2" />
						)}
					</button>
				) : (
					<ActionErrorMsg
						heading="Add to picks"
						message={addToPicks.message}
						className="w-full py-2.5 md:py-3 px-4 min-h-[44px] rounded-xl border border-gray-200 bg-gray-100 text-gray-500 text-sm md:text-base font-medium"
						action={addToPicks.action}
					>
						Add to Picks
					</ActionErrorMsg>
				)}

				{removeFromPicks.possible && (
					<button
						type="button"
						onClick={removeFromLibrary}
						className="w-full py-2.5 md:py-3 px-4 min-h-[44px] border border-red-300 text-red-600 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-red-50 transition-colors duration-200 text-sm md:text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200"
					>
						Remove from Picks
						{isRemovingFromLibrary && (
							<AiOutlineLoading3Quarters className="w-4 h-4 md:w-5 md:h-5 animate-spin ml-2" />
						)}
					</button>
				)}
			</div>
		</div>
	);
};

export default ActionsCard;
